<template>
  <div class="timeline"
       :class="rootModifiers"
  >
    <button class="timeline__head"
            @click="toggleTimeline"
    >
      <app-icon icon="arrow-right-double"
                class="timeline__head-arrow"
      />

      <p class="timeline__title">{{ $t('timeline.title') }}</p>
    </button>

    <div class="timeline__content">
      <button v-if="beforeYear !== null"
              type="button"
              class="timeline__reset-before-year"
              @click="resetBeforeYear"
      >
        <svg>
          <use xlink:href="#clear-cross"></use>
        </svg>
      </button>

      <timeline-images/>

      <div class="timeline__decades hide-scrollbar"
           @mousedown="onMouseDown"
      >
        <timeline-decade v-for="decade in decades"
                         :key="decade.decade"
                         :decade="decade"
                         :is-mouse-down="isMouseDown"
                         :before-year-filter.sync="beforeYear"
        />

        <timeline-selection :before-year="beforeYear"
                            :start-year="startYear"
        />
      </div>

      <img :src="heraldrySrc"
           alt="timeline tree"
           class="timeline__background-image"
           crossorigin="anonymous"
      >

      <button v-if="$bp.isLgOrLess"
              class="timeline__tablet-hide-control mobile-navigator__item"
              @click="isOpen = false"
      >
        <app-icon icon="arrow-right-double"
                  class="btn-icon"
                  is-active
        />
      </button>
    </div>
  </div>
</template>

<script>
import TimelineImages from "@/components/common/TheTimeline/TimelineImages";
import TimelineDecade from "@/components/common/TheTimeline/TimelineDecade";
import TimelineSelection from "@/components/common/TheTimeline/TimelineSelection";
import timelineConfig from "@/config/timeline";
import getDecades from "@/helpers/getDecades";
import { debounce } from "debounce";
import getImgSrc from "@/helpers/getImgSrc";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "TheTimeline",
  components: {
    TimelineImages,
    TimelineDecade,
    TimelineSelection
  },
  data() {
    return {
      config: timelineConfig,
      isMouseDown: false,
      beforeYear: null,
      debouncedSetBeforeYear: null,

      heraldryUrl: process.env.VUE_APP_THEME_HERALDRY_URL
    }
  },
  computed: {
    ...mapState('Filters', {
      'modelBeforeYear': 'beforeYear'
    }),
    ...mapState('Interface', ['isTimelineOpen']),
    ...mapGetters('Coordinates', ['minImageYear']),
    ...mapGetters('Interface', ['isRouteDetailsOpen']),

    isOpen: {
      get()  {
        return this.isTimelineOpen
      },
      set(value) {
        this.setTimelineOpenState(value)
      }
    },
    minImageDecade() {
      return this.minImageYear
        ? Math.floor(this.minImageYear / 10) * 10
        : null
    },
    startYear() {
      return typeof this.minImageDecade === 'number' ? this.minImageDecade - 10 : timelineConfig.minYear
    },
    decades() {
      return getDecades(this.startYear, timelineConfig.maxYear)
    },
    heraldrySrc() {
      return getImgSrc(this.heraldryUrl)
    },
    rootModifiers() {
      return {
        'timeline--is-open': this.isOpen,
        'timeline--is-overlaid': this.isRouteDetailsOpen && this.$bp.isLgOrLess && !this.$bp.isMdOrLess
      }
    }
  },
  methods: {
    toggleTimeline() {
      this.isOpen = !this.isOpen
    },
    onMouseDown() {
      this.isMouseDown = true
    },
    onMouseUp() {
      this.isMouseDown = false
    },
    resetBeforeYear() {
      this.beforeYear = null
    },

    ...mapActions('Filters', ['setBeforeYear']),
    ...mapActions('Interface', ['setTimelineOpenState'])
  },
  watch: {
    $route: {
      deep: true,
      handler: function (newRoute) {
        if (this.$bp.isLgOrLess && newRoute.name !== 'home') {
          this.isOpen = false
        }
      }
    },
    '$bp.isLgOrLess': function (isLgOrLess) {
      this.setTimelineOpenState(!isLgOrLess)
    },
    beforeYear: function (year) {
      this.debouncedSetBeforeYear(year)
    },
    modelBeforeYear: function (modelBeforeYear) {
      if (modelBeforeYear !== this.beforeYear) {
        this.beforeYear = modelBeforeYear
      }
    }
  },
  created() {
    this.beforeYear = this.modelBeforeYear
    this.debouncedSetBeforeYear = debounce(this.setBeforeYear, 500)
    window.addEventListener('mouseup', this.onMouseUp)
  },
  destroyed() {
    window.removeEventListener('mouseup', this.onMouseUp)
  }
}
</script>

<style lang="scss">
.timeline {
  position: absolute;
  top: $content-padding-top;
  bottom: $content-padding-bottom;
  right: 0;
  display: grid;
  grid-template-rows: auto 1fr;
  width: $timeline-width;
  z-index: 20;
  user-select: none;

  &__head {
    position: relative;
    color: $app-text-color;
    padding: 13px 30px 13px 20px;
    background-color: $app-block-color;
    border: none;
    cursor: pointer;
    pointer-events: all;
    box-shadow: 0 2px 4px $app-box-shadow-color;
  }

  &--is-open &__head {
    transform: translateX(0);
  }

  &__head-arrow {
    position: absolute;
    top: 50%;
    left: 26px;
    transform: translateY(-50%);
    transition: transform .3s !important;
  }

  &:not(&--is-open) {
    pointer-events: none !important;
  }
  &:not(&--is-open) &__head-arrow {
    transform: translateY(-50%) rotate(180deg);
  }

  &__title {
    font-size: 2em;
    font-weight: 500;
    text-transform: uppercase;
    text-align: right;
  }

  &__content {
    position: relative;
    height: 100%;
    overflow: hidden;
    background-color: $app-block-color;
    transition: transform .3s;
    transform: translateX(100%);
    box-shadow: 0 4px 4px $app-box-shadow-color;

    &:before {
      content: "";
      position: absolute;
      left: 26px;
      right: 26px;
      top: 0;
      height: 1px;
      background-color: $app-text-color;
    }
  }

  &--is-open &__content {
    transform: translateX(0);
  }

  &__reset-before-year {
    position: absolute;
    top: 15px;
    right: 0;
    cursor: pointer;
    z-index: 5;
    background: transparent;
    border: none;
    color: $app-danger-color;

    svg {
      width: 26px;
      height: 26px;
      display: block;
    }
  }

  &__decades {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: auto;
    padding-top: 40px;
    max-height: 100%;
    z-index: 1;
  }

  &__background-image {
    position: absolute;
    right: -40%;
    bottom: 15%;
    opacity: .15;
    min-height: 50%;
  }
}


@include smallerThan("xl") {
  .timeline {
    width: 200px;

    &__title {
      font-size: 1.5em;
    }
  }
}

@include smallerThan("lg") {
  .timeline {
    width: $mobile-navigator-menu-width;

    &--is-overlaid {
      width: calc($mobile-navigator-menu-width - 45px);
    }

    &__head {
      transition: .3s;
      transform: translateX(100%);
    }

    &--is-overlaid &__head {
      top: 100%;
      transform: translateY(-100%);
      width: 90%;
      margin-left: auto;
    }
    &--is-overlaid#{&}--is-open &__head {
      top: 0;
      transform: translateY(0);
      width: 100%;
    }

    &--is-overlaid &__content {
      transform: translateY(100%);
    }
    &--is-overlaid#{&}--is-open &__content {
      transform: translateY(0);
    }

    &--is-overlaid:not(&--is-open) &__head-arrow {
      transform: translateY(-50%) rotate(-90deg);
    }
    &--is-overlaid#{&}--is-open &__head-arrow {
      transform: translateY(-50%) rotate(90deg);
    }

    &__tablet-hide-control {
      position: absolute;
      left: 0;
      bottom: 0;
      z-index: 1;
      padding: 20px 23px 20px 17px !important;
    }
  }
}

@include smallerThan("md") {
  .timeline {
    top: 0;
    bottom: 0;
    width: 100%;

    &__head {
      display: none;
    }

    &__content {
      display: grid;
      grid-template-columns: 50% 50%;
    }

    &__decades {
      padding-bottom: calc($mobile-navigator-item-size + calc($content-padding-bottom * 2));
    }

    &__tablet-hide-control {
      display: none !important;
    }
  }
}
</style>
